import type { RefObject } from 'react'

import {
    gsap,
    registerGsap,
    useGSAP
} from '@/lib/gsap'

/**
 * Scroll-scrub parallax, recreated from the Webflow SCROLLING_IN_VIEW
 * interactions (IX2 lists a-60, a-80, a-81, a-82, a-89, a-90). Any element can
 * opt in with data attributes, mapping its scroll progress through the viewport
 * to a transform range:
 *
 *   data-parallax              marker (required)
 *   data-parallax-x="0,-60"    xPercent from -> to
 *   data-parallax-y="0,-40"    yPercent from -> to
 *   data-parallax-scale="1,1.1"
 *   data-parallax-opacity="1,0"
 *   data-parallax-start="top bottom"  (ScrollTrigger start, optional)
 *   data-parallax-end="bottom top"    (ScrollTrigger end, optional)
 */
const range = (value: string): [number, number] => {
    const [from, to] = value.split(',').map(part => Number(part.trim()))
    return [from, to]
}

export const useParallax = (scope: RefObject<HTMLElement | null>): void => {
    useGSAP(
        () => {
            registerGsap()

            const root = scope.current
            if (!root) return

            const elements = root.querySelectorAll<HTMLElement>('[data-parallax]')

            elements.forEach(element => {
                const fromVars: gsap.TweenVars = {}
                const toVars: gsap.TweenVars = { ease: 'none' }
                const data = element.dataset

                if (data.parallaxX) {
                    const [from, to] = range(data.parallaxX)
                    fromVars.xPercent = from
                    toVars.xPercent = to
                }

                if (data.parallaxY) {
                    const [from, to] = range(data.parallaxY)
                    fromVars.yPercent = from
                    toVars.yPercent = to
                }

                if (data.parallaxScale) {
                    const [from, to] = range(data.parallaxScale)
                    fromVars.scale = from
                    toVars.scale = to
                }

                if (data.parallaxOpacity) {
                    const [from, to] = range(data.parallaxOpacity)
                    fromVars.opacity = from
                    toVars.opacity = to
                }

                gsap.fromTo(element, fromVars, {
                    ...toVars,
                    scrollTrigger: {
                        trigger: element,
                        start: data.parallaxStart || 'top bottom',
                        end: data.parallaxEnd || 'bottom top',
                        scrub: true
                    }
                })
            })
        },
        { scope }
    )
}
